import React, { useEffect, useMemo, useState } from "react";
import { api, toISO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, StickyNote, Save } from "lucide-react";
import { toast } from "sonner";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const pad = (n) => String(n).padStart(2, "0");

export default function Notes() {
  const [vehicles, setVehicles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vehicleId, setVehicleId] = useState("__all");
  const [notes, setNotes] = useState([]);

  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [calMonth, setCalMonth] = useState(new Date());

  const [openForm, setOpenForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [form, setForm] = useState({ vehicle_id: "", date: "", category: "", notes: "" });

  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) {
      years.push(String(y));
    }
    return years;
  }, [now]);

  const range = useMemo(() => {
    const y = Number(year);
    const m = Number(month);
    const lastDay = new Date(y, m, 0).getDate();
    return { start: `${y}-${pad(m)}-01`, end: `${y}-${pad(m)}-${pad(lastDay)}` };
  }, [year, month]);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data));
    api.get("/note-categories").then((r) => setCategories(r.data.categories || []));
  }, []);

  const loadNotes = () => {
    const params = { start: range.start, end: range.end };
    if (vehicleId !== "__all") params.vehicle_id = vehicleId;
    api.get("/notes", { params }).then((r) => setNotes(r.data));
  };

  useEffect(loadNotes, [vehicleId, range.start, range.end]);

  // Update calendar view when month changes
  useEffect(() => {
    setCalMonth(new Date(Number(year), Number(month) - 1, 1));
  }, [year, month]);

  const vehicleMap = useMemo(() => {
    const m = {};
    vehicles.forEach((v) => (m[v.id] = v));
    return m;
  }, [vehicles]);

  const noteDates = useMemo(
    () => notes.map((n) => new Date(n.date + "T00:00:00")),
    [notes]
  );

  const notesForSelectedDate = useMemo(() => {
    if (!selectedDate) return [];
    const iso = toISO(selectedDate);
    return notes.filter((n) => n.date === iso);
  }, [notes, selectedDate]);

  const openAdd = () => {
    setEditingId(null);
    setForm({
      vehicle_id: vehicleId !== "__all" ? vehicleId : (vehicles[0]?.id || ""),
      date: selectedDate ? toISO(selectedDate) : toISO(new Date()),
      category: "",
      notes: "",
    });
    setOpenForm(true);
  };

  const openEdit = (n) => {
    setEditingId(n.id);
    setForm({
      vehicle_id: n.vehicle_id,
      date: n.date,
      category: n.category || "",
      notes: n.notes || "",
    });
    setOpenForm(true);
  };

  const onSave = async () => {
    if (!form.vehicle_id || !form.date) {
      toast.error("Vehicle and date are required");
      return;
    }
    try {
      if (editingId) {
        await api.put(`/notes/${editingId}`, {
          date: form.date,
          category: form.category,
          notes: form.notes,
        });
        toast.success("Note updated");
      } else {
        await api.post("/notes", form);
        toast.success("Note added");
      }
      setOpenForm(false);
      loadNotes();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    await api.delete(`/notes/${id}`);
    toast.success("Deleted");
    loadNotes();
  };

  return (
    <div className="space-y-6" data-testid="notes-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="label-eyebrow">Log</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-1">
            Important Notes
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Record maintenance events, tax, fitness, accidents & more — per vehicle, per date.
          </p>
        </div>
        <Button onClick={openAdd} className="gap-2" data-testid="add-note-btn">
          <Plus className="h-4 w-4" /> Add Note
        </Button>
      </div>

      <div className="card-swiss p-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <Label className="label-eyebrow">Vehicle</Label>
            <Select value={vehicleId} onValueChange={setVehicleId}>
              <SelectTrigger className="mt-1" data-testid="notes-vehicle-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All vehicles</SelectItem>
                {vehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name} · {v.registration_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow">Year</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="mt-1" data-testid="notes-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map((y) => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow">Month</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="mt-1" data-testid="notes-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTH_NAMES.map((name, i) => (
                  <SelectItem key={name} value={String(i + 1)}>
                    {name.slice(0, 3)} {String(year).slice(-2)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
        <div className="card-swiss p-4">
          <div className="label-eyebrow mb-2">Calendar</div>
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            month={calMonth}
            onMonthChange={setCalMonth}
            modifiers={{ hasNote: noteDates }}
            modifiersClassNames={{ hasNote: "has-entry" }}
            className="rounded-md"
          />
          <div className="mt-3 px-2 flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-600" /> Has note
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-3 font-mono">
            {range.start} → {range.end} · {notes.length} note{notes.length !== 1 ? "s" : ""}
          </p>
        </div>

        <div className="card-swiss p-5" data-testid="notes-list">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-eyebrow">
                {selectedDate ? `Notes on ${toISO(selectedDate)}` : "All notes this month"}
              </div>
            </div>
            {selectedDate && (
              <button
                type="button"
                onClick={() => setSelectedDate(null)}
                className="text-xs text-gray-600 underline hover:text-black"
              >
                Clear date
              </button>
            )}
          </div>
          {(selectedDate ? notesForSelectedDate : notes).length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-500">
              <StickyNote className="h-8 w-8 mx-auto text-gray-300" />
              <p className="mt-2">No notes {selectedDate ? "on this date" : "in this month"}.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {(selectedDate ? notesForSelectedDate : notes).map((n) => (
                <div
                  key={n.id}
                  className="border border-gray-200 rounded-md p-3"
                  data-testid={`note-item-${n.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center flex-wrap gap-2">
                        <span className="text-[11px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm bg-black text-white">
                          {n.category || "Uncategorised"}
                        </span>
                        <span className="text-xs text-gray-500 font-mono">
                          {n.date}
                        </span>
                        <span className="text-xs text-gray-500">·</span>
                        <span className="text-xs font-semibold">
                          {vehicleMap[n.vehicle_id]?.name || "—"}
                        </span>
                        <span className="text-[11px] text-gray-500 font-mono">
                          {vehicleMap[n.vehicle_id]?.registration_number || ""}
                        </span>
                      </div>
                      {n.notes && (
                        <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">
                          {n.notes}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => openEdit(n)}
                        data-testid={`edit-note-${n.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => onDelete(n.id)}
                        data-testid={`delete-note-${n.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent data-testid="note-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {editingId ? "Edit Note" : "New Note"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Vehicle</Label>
              <Select
                value={form.vehicle_id}
                onValueChange={(v) => setForm({ ...form, vehicle_id: v })}
                disabled={!!editingId}
              >
                <SelectTrigger data-testid="note-vehicle-select">
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.name} · {v.registration_number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="note-date-input"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select
                value={form.category}
                onValueChange={(v) => setForm({ ...form, category: v })}
              >
                <SelectTrigger data-testid="note-category-select">
                  <SelectValue placeholder="Pick a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                rows={4}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Details, cost, workshop name, etc."
                data-testid="note-text-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={onSave} className="gap-2" data-testid="save-note-btn">
              <Save className="h-4 w-4" /> {editingId ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
